import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  CarOutlined,
  ShopOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import {
  Card,
  Divider,
  Modal,
  Space,
  Spin,
  Tag,
  Timeline,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { IAwb } from "../../models/Tracking";
import { useGetAwbDetailByCode } from "../../query/Tracking";
interface IProps {
  awbCode: string;
  openDetailModal: boolean;
  setOpenDetailModal: (open: boolean) => void;
}
const ModalAwbDetail = ({
  awbCode,
  openDetailModal,
  setOpenDetailModal,
}: IProps) => {
  const { Text, Title } = Typography;
  const { data: awbDetail, isLoading: awbDetailLoading } =
    useGetAwbDetailByCode(awbCode!);

  const renderStatus = (status: IAwb["awbStatus"]) => {
    switch (status) {
      case "CREATED":
        return <Tag color="blue">Đã tạo</Tag>;
      case "DELIVERED":
        return <Tag color="green">Đã giao</Tag>;
      case "IN_TRANSIT":
        return <Tag color="orange">Đang vận chuyển</Tag>;
      case "PICKED_UP":
        return <Tag color="cyan">Đã lấy hàng</Tag>;
      case "ARRIVED_AT_HUB":
        return <Tag color="purple">Đã đến bưu cục</Tag>;
      case "OUT_FOR_DELIVERY":
        return <Tag color="gold">Đang giao hàng</Tag>;
      case "FAILED":
        return <Tag color="red">Giao thất bại</Tag>;
      default:
        return <Tag color="default">Hoàn hàng</Tag>;
    }
  };

  const getStatusIcon = (status: IAwb["awbStatus"] | null) => {
    switch (status) {
      case "CREATED":
        return <ClockCircleOutlined style={{ color: "#1890ff" }} />;
      case "DELIVERED":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "IN_TRANSIT":
        return <RocketOutlined style={{ color: "#fa8c16" }} />;
      case "PICKED_UP":
        return <ShopOutlined style={{ color: "#13c2c2" }} />;
      case "ARRIVED_AT_HUB":
        return <ShopOutlined style={{ color: "#722ed1" }} />;
      case "OUT_FOR_DELIVERY":
        return <CarOutlined style={{ color: "#faad14" }} />;
      case "FAILED":
        return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
      default:
        return <SyncOutlined style={{ fontSize: "16px", color: "#d9d9d9" }} />;
    }
  };

  return (
    <>
      <Modal
        title={`Thông tin vận đơn ${awbCode}`}
        open={openDetailModal}
        onCancel={() => setOpenDetailModal(false)}
        onOk={() => setOpenDetailModal(false)}
        width={900}
      >
        {awbDetailLoading ? (
          <div className="flex justify-center items-center py-20">
            <Spin tip="Đang tải chi tiết..." />
          </div>
        ) : !awbDetail ? (
          <div className="flex justify-center items-center py-20">
            <Text type="secondary" className="text-base">
              Không tìm thấy thông tin vận đơn
            </Text>
          </div>
        ) : (
          <div className="space-y-6">
            <Card className="bg-blue-50 dark:bg-blue-900">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Text type="secondary" className="text-sm">
                    Mã vận đơn
                  </Text>
                  <Title level={4} className="mt-1 mb-0">
                    {awbDetail.awbCode}
                  </Title>
                </div>
                <div>
                  <Text type="secondary" className="text-sm">
                    Trạng thái
                  </Text>
                  <div className="mt-1">
                    {renderStatus(awbDetail.awbStatus)}
                  </div>
                </div>
                <div>
                  <Text type="secondary" className="text-sm">
                    Mã nhà vận chuyển
                  </Text>
                  <Title level={5} className="mt-1 mb-0">
                    {awbDetail.carrierCode}
                  </Title>
                </div>
                <div>
                  <Text type="secondary" className="text-sm">
                    Tên nhà vận chuyển
                  </Text>
                  <Title level={5} className="mt-1 mb-0">
                    {awbDetail.carrierName}
                  </Title>
                </div>
              </div>
            </Card>

            <Divider className="my-4" />

            <div className="max-h-96 overflow-y-auto p-2">
              {awbDetail.histories && awbDetail.histories.length > 0 ? (
                <Timeline
                  items={awbDetail.histories.map((history) => ({
                    dot: getStatusIcon(history.status),
                    children: (
                      <div className="pb-4">
                        <Text strong className="block text-base mb-2">
                          {history.description}
                        </Text>
                        <Space className="mt-2" wrap>
                          <Tag icon={<ClockCircleOutlined />}>
                            {dayjs(history.eventTime).format(
                              "DD/MM/YYYY HH:mm"
                            )}
                          </Tag>
                          <Tag icon={<EnvironmentOutlined />}>
                            {history.location}
                          </Tag>
                          <Tag color="blue">
                            {history.status === "DELIVERED" && "Đã giao"}
                            {history.status === "IN_TRANSIT" &&
                              "Đang vận chuyển"}
                            {history.status === "CREATED" && "Đã tạo"}
                            {history.status === "PICKED_UP" && "Đã lấy hàng"}
                            {history.status === "ARRIVED_AT_HUB" &&
                              "Đã đến bưu cục"}
                            {history.status === "OUT_FOR_DELIVERY" &&
                              "Đang giao hàng"}
                            {history.status === "FAILED" && "Giao thất bại"}
                            {!history.status && "Hoàn hàng"}
                          </Tag>
                          {history.source && (
                            <Tag color="default">
                              {history.source === "MANUAL" && "Thủ công"}
                              {history.source === "API" && "Tự động"}
                            </Tag>
                          )}
                        </Space>
                      </div>
                    ),
                  }))}
                />
              ) : (
                <div className="text-center py-8">
                  <Text type="secondary">Chưa có lịch sử vận chuyển</Text>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ModalAwbDetail;
